import type { AuthState } from "@/context/AuthContext";
import {
  ArrowUpRightOutlineIcon,
  BankIcon,
  BubbleFilledIcon,
  EtfsIcon,
  InsightDataIcon,
  KlardaLogo1,
  RadarIcon,
  RocketIcon,
  WifiIcon,
} from "@/svg";
import { ApiFilled, AppstoreAddOutlined, DownOutlined, StarFilled } from "@ant-design/icons";
import { Button } from "antd";
import type { HeaderMenuItem } from "./HeaderDropdownMenu";

export const buildMenuStructure = (t: (key: string) => string): HeaderMenuItem[] => [
  {
    key: "/markets",
    label: t("markets"),
  },
  {
    key: "/news",
    label: (
      <div className="flex items-center gap-2">
        {t("news")} <DownOutlined />
      </div>
    ),
    children: [
      {
        key: "/news",
        label: "Trusted Aggregated Crypto News",
        description: "Exclusive Klarda Insights",
        icon: <WifiIcon />,
      },
      {
        key: "/news/vietnamese",
        label: (
          <div className="flex items-center gap-2">
            News Vietnamese <ArrowUpRightOutlineIcon />
          </div>
        ),
        description: "24H Crypto News, Insights, Crypto Airdrop in Vietnamese",
        icon: <WifiIcon />,
      },
    ],
  },
  {
    key: "/insights",
    label: (
      <div className="flex items-center gap-2">
        {t("insight")} <DownOutlined />
      </div>
    ),
    children: [
      {
        key: "/insight",
        label: "Insights",
        description: "On-chain Data & Market Trends",
        icon: <InsightDataIcon />,
      },
      {
        key: "/etfs",
        label: "ETFs",
        description: "Crypto ETFs Info & Performance & News",
        icon: <EtfsIcon />,
      },
      {
        key: "/exchange",
        label: "Exchanges",
        description: "Top Exchanges Ranking, Insights, News",
        icon: <BankIcon />,
      },
    ],
  },
  {
    key: "/products",
    label: (
      <div className="flex items-center gap-2">
        Products <AppstoreAddOutlined />
      </div>
    ),
    children: [
      {
        key: "/screeners",
        label: (
          <div className="flex items-center gap-2">
            Screeners
            <ArrowUpRightOutlineIcon />
          </div>
        ),
        description: "The Best Crypto Screener To Spot Trends",
        icon: <RadarIcon />,
        href: "https://screener.klarda.com/",
      },
      {
        key: "/bubbles",
        label: (
          <div className="flex items-center gap-2">
            Bubbles
            <ArrowUpRightOutlineIcon />
          </div>
        ),
        description: "Crypto Bubbles Map & Trending Watchlists",
        icon: <BubbleFilledIcon />,
        href: "https://bubbles.klarda.com",
      },
      {
        key: "/watchlists",
        label: "Watchlists",
        description: "The most powerful crypto watchlist to track top coins fast!",
        icon: <StarFilled className="text-primary" />,
        href: "/watchlists",
      },
      // {
      //   key: "/alerts",
      //   label: "Alerts",
      //   description: "Instant crypto alert to catch every market move",
      //   icon: <BellIcon />,
      //   href: "https://dashboard.klarda.com/alert",
      // },
      {
        key: "/apis",
        label: "APIs",
        description: "The fastest and most powerful for building Web3 products",
        icon: <ApiFilled className="text-primary" />,
        href: "https://inside.klarda.com/apis",
      },
      {
        key: "/advertise",
        label: "Advertise",
        description: "Work with Klarda Media to growth users & branding",
        icon: <RocketIcon />,
      },
    ],
  },
];

/**
 * Build mobile menu items based on header menu structure
 */
export const buildMobileMenuItems = (
  menuStructure: HeaderMenuItem[],
  handleMenuClick: (path: string) => void,
  t: (key: string) => string,
  authState: AuthState,
  logout: () => void,
  openLoginModal: () => void,
) => {
  const items = [];

  // Add all menu items with their children
  menuStructure.forEach(item => {
    if (!item.children) {
      items.push({
        key: item.key,
        label: item.label,
        onClick: () => handleMenuClick(item.key),
      });
    } else {
      // Add parent as a group title
      items.push({
        key: `${item.key}-group`,
        type: "group",
        label: <span className="font-semibold text-gray-800">{item.label}</span>,
      });

      // Add children
      item.children.forEach(child => {
        items.push({
          key: child.href || child.key,
          label: (
            <div className="flex items-start gap-3 pl-4">
              {child.icon && <div className="mt-1">{child.icon}</div>}
              <div>
                <div className="font-medium">{child.label}</div>
                {child.description && <div className="text-sm text-gray-500">{child.description}</div>}
              </div>
            </div>
          ),
          onClick: () => {
            handleMenuClick(child.href || child.key);
          },
        });
      });

      // Add divider
      items.push({
        key: `${item.key}-divider`,
        type: "divider",
      });
    }
  });

  // Add authentication items
  if (authState === "logged") {
    items.push({
      key: "acc-setting",
      label: (
        <Button
          type="primary"
          className="border-none w-full"
          onClick={e => {
            e.stopPropagation();
            handleMenuClick("/account");
          }}
        >
          {t("account_setting")}
        </Button>
      ),
    });
    items.push({
      key: "signout",
      label: (
        <Button
          type="primary"
          className="w-full border-none text-center"
          onClick={e => {
            e.stopPropagation();
            logout();
          }}
        >
          {t("sign_out")}
        </Button>
      ),
    });
  } else {
    items.push({
      key: "sign-in",
      label: (
        <Button
          type="primary"
          className="w-full text-white border-none text-center"
          onClick={e => {
            e.stopPropagation();
            openLoginModal();
          }}
        >
          <div className="flex justify-center gap-2 items-center">
            <KlardaLogo1 /> {t("sign_in")}
          </div>
        </Button>
      ),
    });
  }

  return items;
};

